import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Factory, Fuel, FlaskConical, Sun, Zap, Atom, Leaf } from 'lucide-react';
import { services } from '../mock';
import { PageHero } from './AboutPage';

const iconMap = { Factory, Fuel, FlaskConical, Sun, Zap, Atom };

const details = {
  cbg: {
    image: 'https://images.pexels.com/photos/9799743/pexels-photo-9799743.jpeg',
    long: 'Our Compressed Biogas plants convert agricultural residue, food waste, and organic biomass into a clean, methane-rich fuel ready to power vehicles, industries and homes. Each plant is built to SATAT specifications and integrated with state-of-the-art purification and bottling systems.',
    features: ['Modular plant designs from 2 to 50 TPD', 'IOC/BPCL/HPCL offtake agreements', 'Turnkey EPC — site to commissioning', 'Smart IoT monitoring and remote diagnostics', 'Bio-slurry to organic fertilizer'],
  },
  'bio-diesel': {
    image: 'https://images.pexels.com/photos/32543708/pexels-photo-32543708.jpeg',
    long: 'High-grade bio-diesel produced from used cooking oil, jatropha, palm and other non-edible feedstocks. Drop-in fuel for existing diesel engines with significantly lower particulate and sulfur emissions.',
    features: ['BIS-certified B100 and blended fuels', 'Continuous-flow trans-esterification plants', 'Quality testing labs at every site', 'Logistics across 18 states', 'Carbon credit advisory'],
  },
  'bio-ethanol': {
    image: 'https://images.pexels.com/photos/32408717/pexels-photo-32408717.jpeg',
    long: 'Sustainable bio-ethanol for India\'s EBP (Ethanol Blending Programme). Produced from sugarcane, maize, broken rice and 2G feedstocks, our ethanol drives cleaner transport and energy independence.',
    features: ['Aligned with EBP-25 target', '1G and 2G feedstock flexibility', 'Closed-loop water and energy systems', 'Distillery design & engineering', 'Off-grid plant operations'],
  },
  ecosolar: {
    image: 'https://images.pexels.com/photos/37061433/pexels-photo-37061433.jpeg',
    long: 'End-to-end solar solutions — rooftop, ground-mount, hybrid and floating. Premium Tier-1 panels with 25-year performance warranty, expert design, installation and lifetime monitoring.',
    features: ['Residential, C&I, and utility-scale', 'Net-metering & grid synchronisation', 'Battery storage and microgrids', 'Performance-linked AMC plans', '5–25 year warranty options'],
  },
  'ev-charging': {
    image: 'https://images.pexels.com/photos/27637329/pexels-photo-27637329.jpeg',
    long: 'A future-ready EV charging ecosystem — AC slow, DC fast and ultra-fast stations for homes, fleets, malls, highways and societies. Software-defined network with seamless user app.',
    features: ['7.4kW to 240kW chargers', 'OCPP-compliant smart network', 'Mobile app + RFID + UPI payments', 'Site selection & franchise opportunities', '24/7 SOC monitoring'],
  },
  'green-hydrogen': {
    image: 'https://images.pexels.com/photos/27863809/pexels-photo-27863809.jpeg',
    long: 'Green hydrogen produced via electrolysis powered by renewable electricity — the cleanest molecule for industry, mobility and storage. Pilot programs underway across heavy industry and refining clusters.',
    features: ['PEM and Alkaline electrolysers', 'On-site H2 storage and dispensing', 'Industrial decarbonisation consulting', 'Government scheme advisory', 'Integration with solar/wind power'],
  },
};

const ServicesPage = () => {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);

  if (!service) {
    return (
      <div>
        <PageHero title="Our Services" subtitle="Comprehensive renewable energy solutions for every scale." />
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Leaf;
              return (
                <Link key={s.id} to={`/services/${s.id}`} className="group bg-white rounded-3xl p-7 border border-slate-100 hover:border-emerald-200 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl hover:shadow-emerald-100">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-md shadow-emerald-200">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="mt-5 text-xl font-extrabold text-[#0b1a1a]">{s.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-sm">{s.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">Learn More <ArrowRight className="w-4 h-4" /></div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Leaf;
  const detail = details[service.id] || {};

  return (
    <div>
      <PageHero title={service.title} subtitle={service.desc} />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-emerald-100">
            <img src={detail.image} alt={service.title} className="w-full h-[480px] object-cover" />
          </div>
          <div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-md shadow-emerald-200">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <h2 className="mt-6 text-4xl md:text-5xl font-extrabold text-[#0b1a1a] leading-tight">{service.title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">{detail.long}</p>
            <div className="mt-8 space-y-3">
              {detail.features?.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  <span className="text-slate-700 font-medium">{f}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-7 py-3.5 rounded-full font-semibold shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all">
              Get a Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
